import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Phone country codes
const PHONE_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+1', country: 'CA', flag: '🇨🇦' },
  { code: '+44', country: 'GB', flag: '🇬🇧' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+351', country: 'PT', flag: '🇵🇹' },
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+32', country: 'BE', flag: '🇧🇪' },
  { code: '+41', country: 'CH', flag: '🇨🇭' },
  { code: '+43', country: 'AT', flag: '🇦🇹' },
  { code: '+46', country: 'SE', flag: '🇸🇪' },
  { code: '+47', country: 'NO', flag: '🇳🇴' },
  { code: '+45', country: 'DK', flag: '🇩🇰' },
  { code: '+358', country: 'FI', flag: '🇫🇮' },
  { code: '+48', country: 'PL', flag: '🇵🇱' },
  { code: '+420', country: 'CZ', flag: '🇨🇿' },
  { code: '+36', country: 'HU', flag: '🇭🇺' },
  { code: '+40', country: 'RO', flag: '🇷🇴' },
  { code: '+30', country: 'GR', flag: '🇬🇷' },
  { code: '+353', country: 'IE', flag: '🇮🇪' },
  { code: '+352', country: 'LU', flag: '🇱🇺' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+54', country: 'AR', flag: '🇦🇷' },
  { code: '+56', country: 'CL', flag: '🇨🇱' },
  { code: '+57', country: 'CO', flag: '🇨🇴' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+972', country: 'IL', flag: '🇮🇱' },
  { code: '+90', country: 'TR', flag: '🇹🇷' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+234', country: 'NG', flag: '🇳🇬' },
  { code: '+254', country: 'KE', flag: '🇰🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+60', country: 'MY', flag: '🇲🇾' },
  { code: '+66', country: 'TH', flag: '🇹🇭' },
  { code: '+84', country: 'VN', flag: '🇻🇳' },
  { code: '+63', country: 'PH', flag: '🇵🇭' },
  { code: '+62', country: 'ID', flag: '🇮🇩' },
  { code: '+380', country: 'UA', flag: '🇺🇦' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
];
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { toast } from 'react-toastify';

// Plan card data for Select Plan step
interface PlanOption {
  name: string;
  price: string;
  priceYearly: string;
  credits: string;
  creditsYearly: string;
  features: string[];
  isPopular?: boolean;
  isFree?: boolean;
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    name: 'Free',
    price: '$0',
    priceYearly: '$0',
    credits: '40 credits per month',
    creditsYearly: '',
    features: ['Access to Private Chat', 'Access to Organization', 'Statistics and reports'],
    isFree: true,
  },
  {
    name: 'Starter',
    price: '$99',
    priceYearly: '$999',
    credits: '500 credits per month',
    creditsYearly: '6,000 credits per year',
    features: ['Access to Private Chat', 'Access to Organization', 'Statistics and reports', 'Private Bridge Nodes'],
  },
  {
    name: 'Growth',
    price: '$299',
    priceYearly: '$2,999',
    credits: '2,000 credits per month',
    creditsYearly: '24,000 credits per year',
    features: ['Access to Private Chat', 'Access to Organization', 'Statistics and reports', 'Private Bridge Nodes', 'Unlimited Team Members'],
    isPopular: true,
  },
  {
    name: 'Scale',
    price: '$999',
    priceYearly: '$9,999',
    credits: '10,000 credits per month',
    creditsYearly: '120,000 credits per year',
    features: ['Access to Private Chat', 'Access to Organization', 'Statistics and reports', 'Private Bridge Nodes', 'Unlimited Team Members'],
  },
];

interface OrganizationUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  featureName?: string;
}

const OrganizationUpgradeModal: React.FC<OrganizationUpgradeModalProps> = ({
  open,
  onClose,
  onSuccess,
  featureName = 'Organization features',
}) => {
  const { apiClient } = useApi();
  const { globalData, refreshGlobalData } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();
  
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');
  const [companyAddress1, setCompanyAddress1] = useState('');
  const [companyAddress2, setCompanyAddress2] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyZipcode, setCompanyZipcode] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyCountry, setCompanyCountry] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [companyWebsite, setCompanyWebsite] = useState('');

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState<string>('Growth');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);

  // Load subscription prices from API
  useEffect(() => {
    if (!open) return;
    const loadPrices = async () => {
      try {
        const response = await withTokenRefresh(() => apiClient.getSubscriptions());
        if (response.success && response.data) {
          setSubscriptions(response.data);
        }
      } catch { /* ignore */ }
    };
    loadPrices();
  }, [open, withTokenRefresh, apiClient]);

  // Map UI plan names to API names (backend may use Growth/Pro, Scale/Business)
  const planNameToApiNames = (planName: string): string[] => {
    const lower = planName.toLowerCase();
    if (lower === 'growth') return ['growth', 'pro'];
    if (lower === 'scale') return ['scale', 'business'];
    return [lower];
  };

  const findPlan = (planName: string): typeof subscriptions[0] | undefined => {
    const exact = subscriptions.find((s: any) => s.name.toLowerCase() === planName.toLowerCase());
    if (exact) return exact;
    const apiNames = planNameToApiNames(planName);
    return subscriptions.find((s: any) => apiNames.includes(s.name.toLowerCase()));
  };

  // Get dynamic price from API if available
  const getDynamicPrice = (planName: string): string | null => {
    if (subscriptions.length === 0) return null;
    const plan = findPlan(planName);
    if (!plan?.periods) return null;
    const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
    const period = plan.periods.find((p: any) => p.periodType === periodType);
    if (!period) return null;
    const n = Number(period.price);
    return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
  };

  // Get dynamic credits from API if available
  const getDynamicCredits = (planName: string): string | null => {
    if (subscriptions.length === 0) return null;
    const plan = findPlan(planName);
    if (!plan?.periods) return null;
    const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
    const period = plan.periods.find((p: any) => p.periodType === periodType);
    if (period?.creditsGranted != null) {
      return `${period.creditsGranted} credits per ${billingPeriod === 'yearly' ? 'year' : 'month'}`;
    }
    return null;
  };

  const steps = ['Organization Info', 'Select Plan', 'Confirm'];

  const handleNext = () => {
    if (activeStep === 0) {
      if (!companyName.trim()) {
        toast.error('Please enter your organization name');
        return;
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const handleUpgrade = async () => {
    const orgId = globalData?.organization?.id;
    if (!orgId) {
      toast.error('Unable to upgrade: Organization not found');
      return;
    }

    const isFreePlan = selectedPlan.toLowerCase() === 'free';
    setIsSubmitting(true);
    try {
      // Step 1: Update organization with new info and set is_business to true
      const fullPhone = companyPhone.trim() ? `${phoneCountryCode}${companyPhone.trim()}` : undefined;
      const body = {
        name: companyName.trim(),
        domain: companyDomain.trim() || undefined,
        address1: companyAddress1.trim() || undefined,
        address2: companyAddress2.trim() || undefined,
        city: companyCity.trim() || undefined,
        zipcode: companyZipcode.trim() || undefined,
        state: companyState.trim() || undefined,
        country: companyCountry.trim() || undefined,
        phone: fullPhone,
        website: companyWebsite.trim() || undefined,
        is_business: true,
      } as any;

      const resp = await withTokenRefresh(() => apiClient.updateOrganization(orgId, body));
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to upgrade. Please try again.');
        return;
      }

      // Step 2: For paid plans, redirect to Stripe Checkout
      if (!isFreePlan) {
        const plan = findPlan(selectedPlan);
        if (!plan?.periods) {
          toast.error(`Plan "${selectedPlan}" not found. Please try again.`);
          return;
        }
        const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
        const period = plan.periods.find((p: any) => p.periodType === periodType);
        if (!period) {
          toast.error(`Billing period "${periodType}" not available for this plan.`);
          return;
        }

        const subResp = await withTokenRefresh(() =>
          apiClient.buySubscription({ subscriptionPeriodId: period.id })
        );

        if (subResp?.success) {
          if (subResp.checkoutUrl) {
            toast.success('Redirecting to payment...');
            window.open(subResp.checkoutUrl, '_blank', 'noopener,noreferrer');
          } else {
            toast.success('Subscription activated successfully!');
          }
        } else {
          toast.error(subResp?.message || 'Failed to start checkout. Please try again.');
          return;
        }
      } else {
        toast.success('Successfully upgraded to Organization account!');
      }

      await refreshGlobalData(true); // Force refresh to bypass cache so hasOrgAccess / is_business update immediately

      onClose();
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Upgrade error:', error);
      if (error?.status === 409) {
        toast.error('You already have an active subscription. Please manage it from your account settings.');
      } else {
        toast.error(error?.message || 'Failed to upgrade. Please contact support.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setActiveStep(0);
      setCompanyName('');
      setCompanyDomain('');
      setCompanyAddress1('');
      setCompanyAddress2('');
      setCompanyCity('');
      setCompanyZipcode('');
      setCompanyState('');
      setCompanyCountry('');
      setCompanyPhone('');
      setPhoneCountryCode('+1');
      setCompanyWebsite('');
      setSelectedPlan('Growth');
      setBillingPeriod('monthly');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxWidth: activeStep === 1 ? '680px' : '520px',
          transition: 'max-width 0.3s ease',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid #E5E7EB' }}>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Icon */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              bgcolor: 'rgba(97, 123, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="16" y="16" width="6" height="6" rx="1" />
              <rect x="2" y="16" width="6" height="6" rx="1" />
              <rect x="9" y="2" width="6" height="6" rx="1" />
              <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
              <path d="M12 12V8" />
            </svg>
          </Box>

          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#181D27', mb: 0.5 }}>
            Switch to Organization
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#535862' }}>
            Enter your organization details to unlock {featureName.toLowerCase()}
          </Typography>
        </Box>

        {/* Stepper */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: '#617BFF' },
                      '&.Mui-completed': { color: '#617BFF' },
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: '#535862' }}>{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, ...(activeStep === 1 ? { px: 2 } : {}), ...(activeStep === 0 ? { maxHeight: '55vh', overflowY: 'auto' } : {}) }}>
          {activeStep === 0 ? (
            // Step 1: Organization Info
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Organization Name <span style={{ color: '#E11D48' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your organization name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Domain <span style={{ color: '#E11D48' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="acme.com"
                  value={companyDomain}
                  onChange={(e) => setCompanyDomain(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Address Line 1 <span style={{ color: '#E11D48' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="456 Business Ave"
                  value={companyAddress1}
                  onChange={(e) => setCompanyAddress1(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Address Line 2
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Suite 100"
                  value={companyAddress2}
                  onChange={(e) => setCompanyAddress2(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                    City <span style={{ color: '#E11D48' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Los Angeles"
                    value={companyCity}
                    onChange={(e) => setCompanyCity(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                    Zip Code
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="10001"
                    value={companyZipcode}
                    onChange={(e) => setCompanyZipcode(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                    State / Province
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="CA"
                    value={companyState}
                    onChange={(e) => setCompanyState(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                    Country <span style={{ color: '#E11D48' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="US"
                    value={companyCountry}
                    onChange={(e) => setCompanyCountry(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Phone
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Select
                    value={phoneCountryCode}
                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: '120px',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        '&:hover': { borderColor: '#617BFF' },
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#617BFF' },
                    }}
                    renderValue={(value) => {
                      const item = PHONE_CODES.find(c => c.code === value);
                      return item ? `${item.flag} ${item.code}` : value;
                    }}
                  >
                    {PHONE_CODES.map((c, idx) => (
                      <MenuItem key={`${c.code}-${c.country}-${idx}`} value={c.code}>
                        {c.flag} {c.country} ({c.code})
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    fullWidth
                    placeholder="555-0123"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 0.75 }}>
                  Website <span style={{ color: '#E11D48' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="https://acme.com"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>
            </Box>
          ) : activeStep === 1 ? (
            // Step 2: Select Plan
            <Box>
              {/* Billing toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setBillingPeriod('monthly')}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: billingPeriod === 'monthly' ? 600 : 400,
                      bgcolor: billingPeriod === 'monthly' ? '#F3F4F6' : 'transparent',
                      color: '#181D27',
                      px: 2, py: 0.75,
                      borderRadius: 0,
                      '&:hover': { bgcolor: '#F3F4F6' },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setBillingPeriod('yearly')}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: billingPeriod === 'yearly' ? 600 : 400,
                      bgcolor: billingPeriod === 'yearly' ? '#F3F4F6' : 'transparent',
                      color: '#181D27',
                      px: 2, py: 0.75,
                      borderRadius: 0,
                      '&:hover': { bgcolor: '#F3F4F6' },
                    }}
                  >
                    Yearly
                    <Box component="span" sx={{ ml: 0.5, fontSize: '10px', fontWeight: 700, color: 'white', bgcolor: '#10B981', borderRadius: '3px', px: 0.5, py: '1px' }}>20%</Box>
                  </Button>
                </Box>
              </Box>

              {/* Plan cards grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
                {PLAN_OPTIONS.map((plan) => {
                  const isSelected = selectedPlan === plan.name;
                  const dynamicPrice = getDynamicPrice(plan.name);
                  const dynamicCredits = getDynamicCredits(plan.name);
                  const displayPrice = plan.isFree ? '$0' : (dynamicPrice || (billingPeriod === 'yearly' ? plan.priceYearly : plan.price));
                  const displayCredits = plan.isFree
                    ? (billingPeriod === 'yearly' ? '' : plan.credits)
                    : (dynamicCredits || (billingPeriod === 'yearly' ? plan.creditsYearly : plan.credits));

                  return (
                    <Box
                      key={plan.name}
                      onClick={() => { if (!plan.isFree || billingPeriod === 'monthly') setSelectedPlan(plan.name); }}
                      sx={{
                        border: isSelected ? '2px solid #617BFF' : '1px solid #E5E7EB',
                        borderRadius: '12px',
                        p: 1.5,
                        cursor: (plan.isFree && billingPeriod === 'yearly') ? 'not-allowed' : 'pointer',
                        opacity: (plan.isFree && billingPeriod === 'yearly') ? 0.5 : 1,
                        position: 'relative',
                        transition: 'all 0.2s',
                        bgcolor: isSelected ? 'rgba(97, 123, 255, 0.04)' : 'white',
                        '&:hover': {
                          borderColor: (plan.isFree && billingPeriod === 'yearly') ? '#E5E7EB' : '#617BFF',
                        },
                      }}
                    >
                      {/* Popular badge */}
                      {plan.isPopular && (
                        <Box sx={{
                          position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                          bgcolor: '#617BFF', color: 'white', fontSize: '10px', fontWeight: 600,
                          px: 1, py: '2px', borderRadius: '4px', whiteSpace: 'nowrap',
                        }}>
                          Popular
                        </Box>
                      )}

                      {/* Selected check */}
                      {isSelected && (
                        <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: '#617BFF' }} />
                      )}

                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>{plan.name}</Typography>
                      <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#181D27', lineHeight: 1.2 }}>{displayPrice}</Typography>
                      <Typography sx={{ fontSize: '10px', color: '#717680', mb: 1 }}>
                        {billingPeriod === 'yearly' ? '/year' : '/month'}
                      </Typography>

                      {displayCredits && (
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#617BFF', mb: 1 }}>
                          {displayCredits}
                        </Typography>
                      )}

                      {plan.features.map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'rgba(97,123,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="7" height="7" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </Box>
                          <Typography sx={{ fontSize: '10px', color: '#535862', lineHeight: 1.3 }}>{f}</Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            // Step 3: Confirm
            <Box>
              <Box
                sx={{
                  bgcolor: '#F9FAFB',
                  borderRadius: '12px',
                  p: 2.5,
                  mb: 2.5,
                }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 2 }}>
                  Organization Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '13px', color: '#717680' }}>Name</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{companyName}</Typography>
                  </Box>
                  {companyDomain && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>Domain</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{companyDomain}</Typography>
                    </Box>
                  )}
                  {(companyAddress1 || companyAddress2) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>Address</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27', textAlign: 'right' }}>
                        {[companyAddress1, companyAddress2].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {(companyCity || companyState || companyZipcode) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>City / State</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27', textAlign: 'right' }}>
                        {[companyCity, companyState, companyZipcode].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {companyCountry && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>Country</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{companyCountry}</Typography>
                    </Box>
                  )}
                  {companyPhone && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>Phone</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{phoneCountryCode}{companyPhone}</Typography>
                    </Box>
                  )}
                  {companyWebsite && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#717680' }}>Website</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#181D27' }}>{companyWebsite}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Selected Plan */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography sx={{ fontSize: '13px', color: '#717680' }}>Selected Plan</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#617BFF' }}>{selectedPlan} ({billingPeriod})</Typography>
              </Box>

              {/* Features unlocked */}
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mt: 2.5, mb: 1.5 }}>
                You will unlock:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(PLAN_OPTIONS.find(p => p.name === selectedPlan)?.features || ['Team member management', 'Multiple workspaces', 'Private team chat', 'Advanced access controls', 'Private bridge nodes']).map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(97, 123, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#535862' }}>{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? handleClose : handleBack}
            disabled={isSubmitting}
            sx={{
              flex: 1,
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#D5D7DA',
              color: '#535862',
              fontWeight: 600,
              py: 1.25,
              '&:hover': { borderColor: '#617BFF', bgcolor: 'rgba(97, 123, 255, 0.04)' },
            }}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === 2 ? handleUpgrade : handleNext}
            disabled={isSubmitting || (activeStep === 0 && (!companyName.trim() || !companyDomain.trim() || !companyAddress1.trim() || !companyCity.trim() || !companyCountry.trim() || !companyWebsite.trim()))}
            sx={{
              flex: 1,
              textTransform: 'none',
              borderRadius: '10px',
              bgcolor: '#617BFF',
              fontWeight: 600,
              py: 1.25,
              boxShadow: activeStep === 2 ? '0 4px 12px rgba(97, 123, 255, 0.3)' : 'none',
              '&:hover': { bgcolor: '#5A6FE8' },
              '&:disabled': { bgcolor: '#617BFF', opacity: 0.7 },
            }}
          >
            {activeStep === 2 ? (isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Upgrade to Organization') : 'Continue'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationUpgradeModal;
